import { useState, useEffect } from 'react';
import { getFirestore, doc, getDoc, setDoc } from 'firebase/firestore';
import { createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { auth } from '../firebase';  // adjust import path as needed

const SignUp = ({ onClose }) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [displayName, setDisplayName] = useState('');
    const [role, setRole] = useState('student');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [registrationDisabled, setRegistrationDisabled] = useState(false);
    const [disabledMessage, setDisabledMessage] = useState('');

    // Check registration status on component mount
    useEffect(() => {
        const checkRegistrationStatus = async () => {
            try {
                const db = getFirestore();
                const settingsDoc = await getDoc(doc(db, 'settings', 'registration'));

                if (settingsDoc.exists()) {
                    const settings = settingsDoc.data();
                    setRegistrationDisabled(settings.disabled);
                    setDisabledMessage(settings.message || 'Registration is currently disabled.');
                } else {
                    // If no settings document exists, default to enabled
                    setRegistrationDisabled(false);
                }
            } catch (error) {
                console.error('Error checking registration status:', error);
                setError('Unable to check registration availability.');
            } finally {
                setIsLoading(false);
            }
        };

        checkRegistrationStatus();
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (registrationDisabled) {
            setError(disabledMessage);
            return;
        }

        try {
            setError('');
            const userCredential = await createUserWithEmailAndPassword(auth, email, password);

            if (!userCredential || !userCredential.user) {
                throw new Error('Failed to create user credential');
            }

            await updateProfile(userCredential.user, { displayName });

            // Store user role in Firestore
            const db = getFirestore();
            await setDoc(doc(db, 'users', userCredential.user.uid), {
                email: userCredential.user.email,
                role: role,
                displayName: displayName,
                createdAt: new Date().toISOString()
            });

            onClose();
        } catch (error) {
            console.error('Registration error:', error);
            let errorMessage = 'Registration failed: ';

            if (error.code) {
                switch (error.code) {
                    case 'auth/email-already-in-use':
                        errorMessage += 'Email is already registered';
                        break;
                    case 'auth/invalid-email':
                        errorMessage += 'Invalid email format';
                        break;
                    case 'auth/operation-not-allowed':
                        errorMessage += 'Email/password accounts are not enabled';
                        break;
                    case 'auth/weak-password':
                        errorMessage += 'Password is too weak';
                        break;
                    default:
                        errorMessage += error.message;
                }
            } else {
                errorMessage += error.message;
            }

            setError(errorMessage);
        }
    };

    if (isLoading) {
        return <div>Loading...</div>;
    }

    return (
        <div>
            <h2>Sign Up</h2>
            {registrationDisabled && (
                <div className="alert alert-warning">
                    {disabledMessage}
                </div>
            )}
            <form onSubmit={handleSubmit}>
                <div>
                    <label>Email:</label>
                    <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        disabled={registrationDisabled}
                    />
                </div>
                <div>
                    <label>Password:</label>
                    <input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        disabled={registrationDisabled}
                    />
                </div>
                <div>
                    <label>Display Name:</label>
                    <input
                        type="text"
                        value={displayName}
                        onChange={(e) => setDisplayName(e.target.value)}
                        required
                        disabled={registrationDisabled}
                    />
                </div>
                <div>
                    <label>Role:</label>
                    <select
                        value={role}
                        onChange={(e) => setRole(e.target.value)}
                        disabled={registrationDisabled}
                    >
                        <option value="student">Student</option>
                        <option value="instructor">Instructor</option>
                    </select>
                </div>
                {error && <div className="error">{error}</div>}
                <button type="submit" disabled={registrationDisabled}>
                    Sign Up
                </button>
            </form>
        </div>
    );
};

export default SignUp;